import type { quat, vec3 } from 'gl-matrix';
import type { AudioSource } from '../audio';
import type { Light } from '../light';
import type { LightFlickerBehaviour } from '../object/behaviours/LightFlickerBehaviour';
import type { Flame } from '../object/primitives/Flame';
import type { RenderObject } from '../object/RenderObject';
import type { Scene } from '../scene/Scene';
import {
  composeAmbientSoundWorldPosition,
  composeFlameWorldPosition,
  composeLightWorldPosition,
  composeMeshWorldTRS,
} from './transform';
import type { CompositeDef, CompositeState, SceneCompositeInstance } from './types';

/**
 * Runtime composite — coordinates a mesh `RenderObject` + a `Light` +
 * per-frame behaviours under a single logical handle. Not itself a
 * `RenderObject` (composites are a logical grouping, not a scene-graph node):
 * children register with their respective managers in the normal way, and
 * both children carry a `compositeInstanceId` tag so picking can escalate.
 *
 * State is a flat bag of typed properties — for `lightEmitter`, just
 * `{ emitLight: 0..1 }`. `setState` accepts a single property or a partial
 * object; the per-frame tick re-evaluates behaviours against the current
 * state value.
 */
export class CompositeInstance {
  readonly instanceId: string;
  readonly compositeId: string;
  readonly def: CompositeDef;
  readonly mesh: RenderObject | null;
  readonly light: Light | null;
  readonly flicker: LightFlickerBehaviour | null;
  /**
   * One Flame per `slots.flames[]` entry. Empty when the composite has no
   * flame slots — the per-frame tick early-outs and there's no per-flame
   * cost when the composite is flame-less.
   */
  readonly flames: readonly Flame[];

  /**
   * Long-lived looping AudioSource bound to the ambient-sound slot, if any.
   * Construction starts it via `play()`; `destroy()` stops + releases it.
   * Position updates flow through `applyTransform` so editor gizmo drags
   * smoothly pan the loop.
   *
   * Mutable because the audio library may not have decoded the clip yet at
   * construction time (scene load fires preload concurrently, not before).
   * `attachAmbientSound` patches in a source once the clip is cached.
   */
  ambientSound: AudioSource | null;

  /**
   * Snapshot of each flame's authored intensity, captured at construction so
   * subsequent `setState({emitLight: x})` calls can re-multiply against the
   * original brightness without accumulating the previous frame's emit value.
   * Indexed in lockstep with `flames`.
   */
  private readonly flameBaseIntensities: readonly number[];

  private state: CompositeState;
  private destroyed = false;
  /**
   * Back-reference used by `setState` to write into `scene.compositeStateOverrides`
   * so runtime state changes survive a tile evict/reload. Required.
   */
  private readonly scene: Scene;

  constructor(args: {
    placement: SceneCompositeInstance;
    def: CompositeDef;
    mesh: RenderObject | null;
    light: Light | null;
    flicker: LightFlickerBehaviour | null;
    flames?: readonly Flame[];
    /**
     * Authored base intensity per flame, sourced from the slot definitions
     * BEFORE any `emit` modulation — see `flameBaseIntensity()` in
     * `flameFactory.ts`. Required when `flames` is non-empty; the per-tick
     * modulation re-multiplies this against the current `emitLight` state,
     * so reverse-engineering it from `flame.intensity` would silently lose
     * the authored value when the composite is constructed with `emit = 0`
     * (the factory pre-multiplies, leaving `flame.intensity = 0` regardless
     * of what the slot asked for).
     */
    flameBaseIntensities?: readonly number[];
    ambientSound?: AudioSource | null;
    initialState: CompositeState;
    scene: Scene;
  }) {
    this.instanceId = args.placement.instanceId;
    this.compositeId = args.placement.compositeId;
    this.def = args.def;
    this.mesh = args.mesh;
    this.light = args.light;
    this.flicker = args.flicker;
    this.flames = args.flames ?? [];
    this.ambientSound = args.ambientSound ?? null;
    this.flameBaseIntensities = args.flameBaseIntensities ?? [];
    if (this.flames.length !== this.flameBaseIntensities.length) {
      throw new Error(
        `CompositeInstance: flames (${this.flames.length}) and ` +
          `flameBaseIntensities (${this.flameBaseIntensities.length}) length mismatch`,
      );
    }
    this.state = { ...args.initialState };
    this.scene = args.scene;
  }

  /**
   * Late-bind the ambient AudioSource for composites whose clip wasn't cached
   * at construction time. Called by the scene host once preload completes.
   * If the composite was destroyed in between, stop the source immediately.
   */
  attachAmbientSound(source: AudioSource): void {
    if (this.destroyed) {
      source.destroy();
      return;
    }
    this.ambientSound?.destroy();
    this.ambientSound = source;
    source.play();
  }

  getState(): Readonly<CompositeState>;
  getState<T extends number | boolean | string>(key: string): T | undefined;
  getState(key?: string): unknown {
    if (key === undefined) return this.state;
    return this.state[key];
  }

  setState(partial: Partial<CompositeState>): void;
  setState(key: string, value: number | boolean | string): void;
  setState(arg1: Partial<CompositeState> | string, arg2?: number | boolean | string): void {
    if (typeof arg1 === 'string') {
      if (arg2 === undefined) return;
      this.state[arg1] = arg2;
    } else {
      Object.assign(this.state, arg1);
    }
    // Persist the full current state into the scene's override map so a tile
    // evict + reload re-applies it (the chunk-loaded placement state would
    // otherwise reset the instance to the publish-time defaults).
    this.scene.compositeStateOverrides.set(this.instanceId, { ...this.state });
  }

  /** Per-frame update. Called by Scene's tick loop. */
  tick(deltaMs: number): void {
    if (this.destroyed) return;
    const emit = typeof this.state['emitLight'] === 'number' ? this.state['emitLight'] : 1;
    this.flicker?.tick(deltaMs, emit);
    // Linear modulation — at emit=0 each flame body goes black (×0 in the
    // shader's `emissive = col * uIntensity * alpha`); at emit=1 each renders
    // at its authored intensity. No coupling to the flicker noise — flame
    // bodies have their own internal scroll/noise; coupling them would force
    // candle-flicker on every flame whether the author wants it or not.
    for (let i = 0; i < this.flames.length; i++) {
      this.flames[i].intensity = this.flameBaseIntensities[i] * emit;
    }
  }

  /**
   * Recompute child mesh + light world TRS from a new placement transform.
   * Used by the scene editor's gizmo to move/rotate a placed composite live
   * during a drag. Delegates to the shared composition helpers so the rules
   * are guaranteed to match lightEmitter's construction-time placement.
   */
  applyTransform(
    position: readonly [number, number, number] | vec3,
    rotation: readonly [number, number, number, number] | quat,
    scale: number,
  ): void {
    if (this.destroyed) return;
    // Synthesize a placement snapshot for the shared helpers — they read
    // position/rotation/scale + slot data and don't care that the rest of
    // the SceneCompositeInstance shape is missing.
    const placement: SceneCompositeInstance = {
      instanceId: this.instanceId,
      compositeId: this.compositeId,
      position: [position[0] as number, position[1] as number, position[2] as number],
      rotation: [
        rotation[0] as number,
        rotation[1] as number,
        rotation[2] as number,
        rotation[3] as number,
      ],
      scale,
    };

    if (this.mesh) {
      const trs = composeMeshWorldTRS(placement, this.def.slots.mesh);
      this.mesh.trs.position[0] = trs.position[0];
      this.mesh.trs.position[1] = trs.position[1];
      this.mesh.trs.position[2] = trs.position[2];
      this.mesh.trs.setRotationQuat(trs.rotation);
      this.mesh.trs.scale = trs.scale;
    }

    if (this.light) {
      const { position: lp } = composeLightWorldPosition(
        placement,
        this.def.slots.light,
        this.def.slots.mesh,
        this.scene,
      );
      this.light.position = [lp[0], lp[1], lp[2]];
      this.light.updateBounds();
    }

    const flameSlots = this.def.slots.flames;
    if (flameSlots && this.flames.length === flameSlots.length) {
      for (let i = 0; i < this.flames.length; i++) {
        const fp = composeFlameWorldPosition(
          placement,
          flameSlots[i],
          this.def.slots.mesh,
          this.scene,
        );
        this.flames[i].position[0] = fp[0];
        this.flames[i].position[1] = fp[1];
        this.flames[i].position[2] = fp[2];
      }
    }

    const ambientSlot = this.def.slots.ambientSound;
    if (this.ambientSound && ambientSlot) {
      this.ambientSound.setPosition(composeAmbientSoundWorldPosition(placement, ambientSlot));
    }
  }

  /** Tear down children (objectManager / lightManager) — idempotent. */
  destroy(scene: Scene): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.light) {
      this.light.shadowMap?.destroy();
      scene.lightManager.removeLight(this.light);
    }
    if (this.mesh) {
      scene.objectManager.removeObject(this.mesh);
    }
    for (const flame of this.flames) {
      const idx = scene.flames.indexOf(flame);
      if (idx >= 0) scene.flames.splice(idx, 1);
    }
    this.ambientSound?.destroy();
  }
}
